import Swiper from 'swiper';
import { Navigation, Thumbs } from 'swiper/modules';
import { swiperNavigation } from '../modules/swiperModules';

class Gallery {
    constructor(el) {
        if (!el) {
            return;
        }

        this.root = el;
        this.init();
    }

    init() {
        this.rootParent = this.root.parentElement;
        this.lightboxButton = this.root.querySelector('[js-lightbox-button]');
        this.lighboxEl = this.rootParent?.querySelector('[js-lightbox]');
        this.closeLightboxButton =
            this.lighboxEl?.querySelector('[js-close-lighbox]');
        this.lighboxThumbnails = this.lighboxEl?.querySelector(
            '.lightbox-thumbnails'
        );
        this.lightboxGallery =
            this.lighboxEl?.querySelector('.lightbox-gallery');
        this.lightboxGalleryImgs =
            this.lightboxGallery?.querySelectorAll('img') || [];
        this.lightboxGalleryArrows =
            this.lighboxEl?.querySelector('.slider-arrows');
        this.arrowsEl = this.root.querySelector('.slider-arrows');

        this.swiperInstance = null;
        this.lightboxThumbnailsSwiperInstance = null;
        this.lightboxSwiperInstance = null;
        this.lightboxActiveClass = '-active';

        this.initLightbox();
        this.initSwiper();
        this.events();

        if (this.rootParent) {
            this.rootParent.style.opacity = '1';
        }
    }

    destroy() {
        this.swiperInstance?.destroy(true, true);
        this.lightboxThumbnailsSwiperInstance?.destroy(true, true);
        this.lightboxSwiperInstance?.destroy(true, true);

        this.swiperInstance = null;
        this.lightboxThumbnailsSwiperInstance = null;
        this.lightboxSwiperInstance = null;
    }

    initLightbox() {
        if (this.lighboxThumbnails) {
            this.lightboxThumbnailsSwiperInstance = new Swiper(
                this.lighboxThumbnails,
                {
                    breakpoints: {
                        0: {
                            slidesPerView: 2.5,
                        },
                        1024: {
                            slidesPerView: 'auto',
                        },
                    },
                }
            );
        }

        if (this.lightboxGallery) {
            this.lightboxSwiperInstance = new Swiper(this.lightboxGallery, {
                slidesPerView: 1,
                modules: [Thumbs, Navigation],
                thumbs: {
                    swiper: this.lightboxThumbnailsSwiperInstance,
                },
                ...swiperNavigation(this.lightboxGalleryArrows),
            });

            this.lightboxSwiperInstance.on('slideChange', e => {
                this.swiperInstance?.slideTo(e.activeIndex);

                if (this.lightboxGalleryImgs) {
                    this.lightboxGalleryImgs.forEach(img => {
                        if (!img._zoomState) {
                            img._zoomState = {
                                scale: 1,
                                x: 0,
                                y: 0,
                                zoomed: false,
                            };
                        }
                        const state = img._zoomState;

                        if (state.zoomed) {
                            state.scale = 1;
                            state.x = 0;
                            state.y = 0;
                            state.zoomed = false;

                            this.applyTransform(img, state);
                            img.classList.remove('-zoommed');
                        }
                    });
                }
            });
        }
    }

    initSwiper() {
        let args = {
            slidesPerView: 1,
            modules: [Navigation],
        };

        if (this.arrowsEl) {
            args = { ...args, ...swiperNavigation(this.arrowsEl) };
        }

        this.swiperInstance = new Swiper(this.root, args);

        this.swiperInstance.on('slideChange', e => {
            this.lightboxSwiperInstance?.slideTo(e.activeIndex);
        });
    }

    events() {
        if (this.closeLightboxButton) {
            this.closeLightboxButton.addEventListener(
                'click',
                this.handleCloseLightbox.bind(this)
            );
        }

        if (this.lightboxButton) {
            this.lightboxButton.addEventListener(
                'click',
                this.handleOpenLightbox.bind(this)
            );
        }

        document.addEventListener(
            'keydown',
            this.onLightboxKeydowns.bind(this)
        );

        if (this.lightboxGalleryImgs.length) {
            this.lightboxGalleryImgs.forEach(img => {
                img.addEventListener(
                    'click',
                    this.onLightboxImgClick.bind(this)
                );
            });
        }
    }

    handleOpenLightbox() {
        this.lighboxEl?.classList.add(this.lightboxActiveClass);
    }

    handleCloseLightbox() {
        this.lighboxEl?.classList.remove(this.lightboxActiveClass);
    }

    onLightboxKeydowns(e) {
        if (!this.lighboxEl?.classList.contains(this.lightboxActiveClass)) {
            return;
        }

        const { key } = e;

        if (key === 'Escape') {
            this.handleCloseLightbox();
        } else if (key === 'ArrowLeft') {
            this.lightboxSwiperInstance?.slidePrev();
        } else if (key === 'ArrowRight') {
            this.lightboxSwiperInstance?.slideNext();
        }
    }

    onLightboxImgClick(e) {
        const img = e.currentTarget;

        if (!img._zoomState) {
            img._zoomState = {
                scale: 1,
                x: 0,
                y: 0,
                zoomed: false,
            };
        }

        const state = img._zoomState;

        if (state.zoomed) {
            state.scale = 1;
            state.x = 0;
            state.y = 0;
            state.zoomed = false;

            this.applyTransform(img, state);
            img.classList.remove('-zoommed');
            return;
        }

        const rect = img.getBoundingClientRect();

        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;

        const newScale = 2.5;

        state.x = mx - (mx - state.x) * (newScale / state.scale);
        state.y = my - (my - state.y) * (newScale / state.scale);

        state.scale = newScale;
        state.zoomed = true;

        this.applyTransform(img, state);
        img.classList.add('-zoommed');
    }

    applyTransform(img, state) {
        img.style.transform = `translate(${state.x}px, ${state.y}px) scale(${state.scale})`;
    }
}

export const initProductGallery = el => {
    if (!el) {
        return null;
    }

    if (el._productGalleryInstance) {
        el._productGalleryInstance.destroy();
        el._productGalleryInstance = null;
    }

    const instance = new Gallery(el);
    el._productGalleryInstance = instance;

    return instance;
};

window.mdaInitProductGallery = initProductGallery;

document.querySelectorAll('.js-gallery').forEach(el => {
    initProductGallery(el);
});
